import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { getSubDomainbySecurityControl } from '../../redux/actions/actionActions'
import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'

import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye'
import { Modal, Paper } from '@mui/material'
import { DataGrid, GridColDef, GridCellParams } from '@mui/x-data-grid'
import { NavLink, useNavigate, useParams } from 'react-router-dom'
import { updateSubDomain } from '../../redux/subDomains/subDomainActions'
import DriveFileRenameOutlineOutlined from '@mui/icons-material/DriveFileRenameOutlineOutlined'
import UseAlert from '../../components/common/alert'
import { useTranslation } from 'react-i18next'
import FixWeightTargetScore from '../../components/containers/FixWeightTargetScoreForm'
import { formatTitle } from '../../utils/formatTitle'
import { formatDecimal } from '../../utils/formatNumbers'
import { numericColumn } from '../../utils/dataGridStyles'
import {
  ListPageShell,
  listPageDataGridSx,
  listPageGridSlots,
  listPageRowActionsStyle,
} from '../../components/common/ListPageShell'
import { useTableLoadingGate } from '../../hooks/useTableLoadingGate'

export default function SubDomainListBySecurityCheckFamily() {
  const { t } = useTranslation()

  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })
  const [search, setSearch] = useState('')

  const [open, setOpen] = React.useState(false)
  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)
  const [SubDomain, setSubDomain] = useState({
    _id: '',
    SubDomainId: '',
    Description: '',
    Weight: 0,
    TargetScore: 0,
    Label: '',
    SecurityControlId: '',
    LabelSCF: '',
  })

  const [page, setPage] = useState<number>(1)
  const [pageSize, setPageSize] = useState<number>(10)

  const handlePageSizeChange = (page: number, newSize: number) => {
    setPage(page)
    setPageSize(newSize)
  }

  const handleInputChangeSub = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setSubDomain((prevSubDomain) => ({
      ...prevSubDomain,
      [name]: value,
    }))
  }

  const handleCloseAlert = () => {
    setAlert({
      ...alert,
      open: false,
    })
    handleClose()
  }

  const dispatch: AppDispatch = useAppDispatch()
  const subDomainsList = useSelector(
    (state: RootState) => state.action.subDomains
  )

  const loading = useSelector((state: RootState) => state.action.loading)
  const navigate = useNavigate()

  const { SecurityId } = useParams<{ SecurityId: string }>()
  const showLoader = useTableLoadingGate(loading, [SecurityId])

  useEffect(() => {
    if (SecurityId) {
      dispatch(getSubDomainbySecurityControl(SecurityId))
    }
  }, [dispatch, SecurityId])

  const filteredRows = useMemo(() => {
    const rows = Array.isArray(subDomainsList) ? subDomainsList : []
    const query = search.trim().toLowerCase()
    if (!query) return rows
    return rows.filter((row: any) =>
      [row.LabelSD, row.Label, row.Description]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    )
  }, [search, subDomainsList])

  const handleLaunchClick = (RowData: any) => {
    setSubDomain(RowData)
    handleOpen()
  }

  const handleUpdateSubDomain = async () => {
    try {
      if (
        SubDomain.Description &&
        SubDomain.TargetScore &&
        SubDomain._id &&
        SubDomain.Label &&
        SubDomain.Weight
      ) {
        await dispatch(
          updateSubDomain({
            updatedSubDomain: {
              _id: SubDomain._id,
              SubDomainId: SubDomain.SubDomainId,
              Description: SubDomain.Description,
              Weight: SubDomain.Weight,
              TargetScore: SubDomain.TargetScore,
              Label: SubDomain.Label,
              SecurityControlId: SubDomain.SecurityControlId,
              LabelSCF: SubDomain.LabelSCF,
            },
            _id: SubDomain._id,
          })
        )

        dispatch(getSubDomainbySecurityControl(SecurityId || ''))

        console.log(SubDomain)
        setAlert({
          open: true,
          message: t('alerts.subDomainUpdate.success'),
          severity: 'success',
        })
      } else {
        setAlert({
          open: true,
          message: t('alerts.subDomainUpdate.emptyFields'),
          severity: 'error',
        })
      }
    } catch (error: any) {
      console.error('Error updating SUB DOMAIN:', error.message)

      setAlert({
        open: true,
        message: t('alerts.subDomainUpdate.error'),
        severity: 'error',
      })
    }
  }
  const columns: GridColDef[] = [
    {
      field: 'Label',
      headerName: t('tables.SubdomainMaturityTable.Label'),
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <NavLink
          className="incyia-name-link line-clamp-2 overflow-hidden text-ellipsis"
          to={`/subdomain/${params.id}`}
        >
          <strong>{formatTitle(params.row.LabelSD as string)}</strong>
        </NavLink>
      ),
    },
    {
      field: 'Description',
      headerName: t('tables.SubdomainMaturityTable.Description'),
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <span className="line-clamp-2 overflow-hidden text-ellipsis">
          {params.value}
        </span>
      ),
    },
    {
      field: 'Weight',
      headerName: t('tables.SubdomainMaturityTable.Weight'),
      flex: 0.4,
      ...numericColumn(110),
      renderCell: (params) => (
        <span className="incyia-num">
          {Number(params.value) < 0 ? '—' : formatDecimal(params.value)}
        </span>
      ),
    },
    {
      field: 'TargetScore',
      headerName: t('tables.SubdomainMaturityTable.TargetScore'),
      flex: 0.4,
      ...numericColumn(130),
      renderCell: (params) => (
        <span className="incyia-num">
          {Number(params.value) < 0 ? '—' : formatDecimal(params.value)}
        </span>
      ),
    },
    {
      field: 'Action',
      headerName: t('tables.SubdomainMaturityTable.Action'),
      minWidth: 100,
      flex: 0.2,
      renderCell: (params: GridCellParams) => {
        if (!params.row) return null

        const { TargetScore, Weight, hasMaturityScoring } = params.row

        return (
          <div className="list-row-actions" style={listPageRowActionsStyle}>
            {TargetScore !== -1 &&
              Weight !== -1 &&
              hasMaturityScoring === true && (
                <NavLink to={`/subDomain-Maturityscoring/${params.id}`}>
                  <RemoveRedEyeIcon
                    style={{
                      cursor: 'pointer',
                      color: '#2c3e50',
                      fontSize: '23px',
                      marginRight: '5px',
                    }}
                  />
                </NavLink>
              )}

            <DriveFileRenameOutlineOutlined
              style={{
                cursor: 'pointer',
                color: '#2c3e50',
                fontSize: '18px',
                marginRight: '5px',
              }}
              onClick={() => handleLaunchClick(params.row)}
            />
          </div>
        )
      },
    },
  ]
  return (
    <>
      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={handleCloseAlert}
      />
      <div className="page-content">
        <ListPageShell
          eyebrow={t('Security Control')}
          title={formatTitle(t('Sub-domains'))}
          subtitle={t('subDomainList.subtitle', {
            defaultValue:
              'Review maturity requirements and target scores for this security control.',
          })}
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={t('filterByName')}
          countLabel={`${filteredRows.length} ${t('Sub-domains')}`}
        >
          <div className="w-full max-w-full overflow-hidden">
            <DataGrid
              loading={showLoader}
              rows={filteredRows}
              columns={columns}
              getRowId={(row) => row._id}
              hideFooter
              sx={listPageDataGridSx(filteredRows.length)}
              slots={listPageGridSlots}
            />
          </div>
        </ListPageShell>

        <Modal open={open} onClose={handleClose}>
          <Paper
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              p: 4,
              minWidth: '300px',
            }}
          >
            <FixWeightTargetScore
              values={SubDomain}
              onChange={handleInputChangeSub}
              onSubmit={handleUpdateSubDomain}
              onCancel={handleClose}
              fields={[
                {
                  type: 'number',
                  label: t('popups.fixWeightTargetScore.weight'),
                  name: 'Weight',
                  required: true,
                },
                {
                  type: 'number',
                  label: t('popups.fixWeightTargetScore.targetScore'),
                  name: 'TargetScore',
                  required: true,
                  fullWidth: true,
                },
              ]}
            />
          </Paper>
        </Modal>
      </div>
    </>
  )
}
